import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePopup from '../../../hooks/usePopup';
import WidgetGalleryContext from '../../../contexts/WidgetGalleryContext';
import DashboardMenu from './DashboardMenu';
import DashboardForm from '../../DashboardForm';
import captureException from '../../../services/captureException';
import IFrameForm from '../../IFrameForm';
import VideoCardForm from '../../VideoCardForm';
import TextCardForm from '../../TextCardForm';
import buildCardLayout from '../../../contextProviders/WidgetGalleryProvider/buildCardLayout';
import createMetricListGadget from '../../../api/metricListGadgets/createMetricListGadget';
import DashboardContext from '../../../contexts/DashboardContext';
import ConfigureVariables from '../ConfigureVariables';
import useTemplateInstances from '../../../hooks/dashboards/useTemplateInstances';
import AnalyticsContext from '../../../contexts/AnalyticsContext';
import findStartingY from '../../../contextProviders/WidgetGalleryProvider/findStartingY';
import CurrentUserContext from '../../../contexts/CurrentUserContext';
import WallboardContext from '../../../contexts/WallboardContext';
import ImageCardEditForm from '../../ImageGadget/ImageCardEditForm';
import AccountPickerContext from '../../../contexts/AccountPickerContext';
import AddScorecardForm from '../AddScorecardForm';
import cardTypeCheckers from '../../../types/cardTypeCheckers';
import aguid from 'lib/aguid';
import { buildDashboardShow } from 'navigation/appRoutes';
import Modal, { ModalTransition } from '@atlaskit/modal-dialog';
import VariableFiltersContext from '../../../contexts/VariableFiltersContext';
import { ScheduleReportModalContainer } from '../../ScheduleReportModal';
import createDashboardScheduleSavedFilter from '../createDashboardScheduleSavedFilter';
import { PortalsContext } from '../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import ContentUsage from '../../ContentUsage';
import FilterPlatesProvider from '../../../contextProviders/FilterPlatesProvider';
import ReportQuickFiltersModal from '../../Report/QuickFilters';
import PinnedFiltersModal from '../../Report/PinnedFilters';
import useDataTypesOnReportCanvas from '../../Report/useDataTypesOnReportCanvas';
import useDashboardFilterBarSettings from '../useDashboardFilterBarSettings';

const DashboardMenuContainer = ({
  startEditing,
  saveDashboardAs,
  createTemplateFrom,
  onDelete,
  setCurrentCanvas,
  canvasMode,
  isTemplate,
  openVariableFiltersSlideOut,
}: {
  startEditing: () => void;
  saveDashboardAs: (name: string, labelIds: string[]) => Promise<void>;
  createTemplateFrom: () => Promise<void>;
  onDelete: () => void;
  setCurrentCanvas: React.Dispatch<React.SetStateAction<Canvas>>;
  canvasMode: CanvasMode;
  isTemplate: boolean;
  openVariableFiltersSlideOut: () => void;
}) => {
  const { selectedPortal } = useContext(PortalsContext);
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { id: currentUserId } = useContext(CurrentUserContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const { isWallboard } = useContext(WallboardContext);
  const { dashboard, templatedFrom } = useContext(DashboardContext);
  const [isCreatedByMe, setIsCreatedByMe] = useState<boolean>(
    !!dashboard && dashboard.createdBy === currentUserId,
  );
  const templateInstances = useTemplateInstances(dashboard);
  const navigate = useNavigate();
  const isDeletable =
    dashboard && dashboard.isTemplate ? templateInstances.length === 0 : true;
  const isTemplated = !!templatedFrom;
  // Its quick and pinned filters, picked from the fields
  // of the datasets on its canvas
  const filterBarSettings = useDashboardFilterBarSettings(dashboard);
  const canvasDataTypes = useDataTypesOnReportCanvas(
    templatedFrom ? templatedFrom.canvas : dashboard?.canvas,
  );
  const {
    isOpen: isQuickFiltersOpen,
    open: openQuickFilters,
    close: closeQuickFilters,
  } = usePopup(false);
  const {
    isOpen: isPinnedFiltersOpen,
    open: openPinnedFilters,
    close: closePinnedFilters,
  } = usePopup(false);
  const {
    isOpen: saveAsIsOpen,
    open: openSaveAs,
    close: closeSaveAs,
  } = usePopup(false);
  const {
    isOpen: isIFrameFormOpen,
    open: openIFrameForm,
    close: closeIFrameForm,
  } = usePopup(false);
  const {
    isOpen: isImageCardFormOpen,
    open: openImageCardForm,
    close: closeImageCardForm,
  } = usePopup(false);
  const {
    isOpen: isVideoCardFormOpen,
    open: openVideoCardForm,
    close: closeVideoCardForm,
  } = usePopup(false);
  const {
    isOpen: isTextCardFormOpen,
    open: openTextCardForm,
    close: closeTextCardForm,
  } = usePopup(false);
  const {
    isOpen: isSettingsOpen,
    open: openSettings,
    close: closeSettings,
  } = usePopup(false);
  const {
    isOpen: isConfigureVariablesOpen,
    open: openConfigureVariables,
    close: closeConfigureVariables,
  } = usePopup(false);
  const {
    isOpen: isAddScorecardFormOpen,
    open: openAddScorecardForm,
    close: closeAddScorecardForm,
  } = usePopup(false);
  const {
    isOpen: isUsageReportOpen,
    open: openUsageReport,
    close: closeUsageReport,
  } = usePopup(false);
  const {
    isOpen: isScheduleOpen,
    open: openSchedule,
    close: closeSchedule,
  } = usePopup(false);

  const { variableFilters } = useContext(VariableFiltersContext);
  const { open: openWGallery } = useContext(WidgetGalleryContext);
  const [isScorecardDashboard, setIsScorecardDashboard] =
    useState<boolean>(false);
  const [savingAs, setSavingAs] = useState(false);

  useEffect(() => {
    setIsCreatedByMe(!!dashboard && dashboard.createdBy === currentUserId);
  }, [currentUserId, dashboard]);

  useEffect(() => {
    setIsScorecardDashboard(
      !!dashboard && dashboard.canvas.cards.some(cardTypeCheckers.isScorecard),
    );
  }, [dashboard]);

  const onStartEditing = () => {
    startEditing();
  };

  const onAddScorecardClicked = useCallback(() => {
    openAddScorecardForm();
  }, [openAddScorecardForm]);

  const onUsageReportClicked = useCallback(() => {
    openUsageReport();
  }, [openUsageReport]);

  const onScheduleClicked = useCallback(() => {
    openSchedule();
  }, [openSchedule]);

  // Snapshots the dashboard's live variable-filter selections so each scheduled
  // delivery replays exactly what the scheduler saw (see PrintDashboardShow).
  const createScheduleSavedFilter = useCallback(
    () =>
      createDashboardScheduleSavedFilter({
        variableDrillDowns: variableFilters,
        accountId: selectedAccountId,
      }),
    [selectedAccountId, variableFilters],
  );

  const onSaveAsClicked = useCallback(() => {
    openSaveAs();
    trackEvent('Dashboard - Menu - Save As - Clicked');
  }, [openSaveAs, trackEvent]);

  if (!dashboard) {
    return null;
  }

  const onCreateNewGadgetClicked = () => {
    trackEvent('Dashboard - Menu - Create new gadget', {
      dashboardId: dashboard.id,
      dashboardName: dashboard.name,
      isWallboardSlide: isWallboard ? 'true' : 'false',
    });
    openWGallery(true);
  };

  const onSaveAs = ({
    name,
    labelIds,
  }: {
    name: string;
    labelIds: string[];
  }) => {
    setSavingAs(true);
    saveDashboardAs(name, labelIds).then(() => {
      setSavingAs(false);
      closeSaveAs();
    });
    trackEvent('Dashboard Saved As New');
  };

  const onFullscreenClicked = () => {
    try {
      const dash = document.getElementById('dashboard');
      if (dash) {
        dash.requestFullscreen();
      }
    } catch (ex) {
      alert('Something went wrong');
      captureException(ex);
    }
  };

  const onExitFullscreenClicked = () => {
    try {
      document.exitFullscreen();
    } catch (ex) {
      alert('Something went wrong');
      captureException(ex);
    }
  };

  const onMetricListAdded = async () => {
    const metricListId = aguid();
    await createMetricListGadget({
      id: metricListId,
      name: 'Metric List',
      currentUserId,
      accountId: selectedAccountId,
    });

    setCurrentCanvas((c) => {
      const newCard = (() => {
        return {
          layout: buildCardLayout(0, findStartingY(c.cards, 'desktop')),
          content: {
            type: 'Metric List' as 'Metric List',
            metricListId: metricListId,
          },
        };
      })();

      return {
        ...c,
        cards: [...c.cards, newCard],
      };
    });
    startEditing();
  };

  const onSettingsClicked = () => {
    openSettings();
  };

  const onConfigureVariables = () => {
    openConfigureVariables();
  };

  const onViewSourceTemplate = (templateId: string | undefined) => {
    if (!templateId) {
      const error = new Error();
      error.name = 'Dashboard Template: templateId is missing';
      throw error;
    }

    navigate(buildDashboardShow({ id: templateId, portal: selectedPortal }));
  };

  const isFullScreen = !!document.fullscreenElement;

  return (
    <React.Fragment>
      <DashboardMenu
        onStartEditing={onStartEditing}
        onCreateNewGadgetClicked={onCreateNewGadgetClicked}
        onSaveAs={onSaveAsClicked}
        createTemplateFrom={createTemplateFrom}
        onDelete={() => {
          const confirmed = window.confirm(
            `You are about to delete the ${dashboard.name} dashboard. Are you sure?`,
          );
          if (confirmed) {
            onDelete();
          }
        }}
        isFullScreen={isFullScreen}
        onFullscreenClicked={onFullscreenClicked}
        onExitFullscreenClicked={onExitFullscreenClicked}
        openIFrameForm={openIFrameForm}
        onImageCardAdded={openImageCardForm}
        openVideoCardForm={openVideoCardForm}
        openTextCardForm={openTextCardForm}
        onMetricListAdded={onMetricListAdded}
        openVariableFiltersSlideOut={openVariableFiltersSlideOut}
        isTemplate={isTemplate}
        onSettingsClicked={onSettingsClicked}
        isTemplated={isTemplated}
        onConfigureVariables={onConfigureVariables}
        onViewSourceTemplate={onViewSourceTemplate}
        isDeletable={isDeletable}
        isWallboard={isWallboard}
        onAddScorecardClicked={onAddScorecardClicked}
        onUsageReportClicked={onUsageReportClicked}
        onOpenQuickFilters={filterBarSettings ? openQuickFilters : undefined}
        onOpenPinnedFilters={filterBarSettings ? openPinnedFilters : undefined}
        onScheduleClicked={onScheduleClicked}
        isScorecardDashboard={isScorecardDashboard}
        isCreatedByMe={isCreatedByMe}
        dashboard={dashboard}
      />
      <DashboardForm
        close={closeSaveAs}
        isOpen={saveAsIsOpen}
        onSaveAs={onSaveAs}
        isSaveAs
        isSavingAs={savingAs}
      />
      <AddScorecardForm
        close={closeAddScorecardForm}
        isOpen={isAddScorecardFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      <IFrameForm
        close={closeIFrameForm}
        isOpen={isIFrameFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      {isImageCardFormOpen && (
        <ImageCardEditForm
          close={closeImageCardForm}
          isOpen={isImageCardFormOpen}
          startEditing={startEditing}
          setCurrentCanvas={setCurrentCanvas}
          canvasMode={canvasMode}
        />
      )}
      <VideoCardForm
        close={closeVideoCardForm}
        isOpen={isVideoCardFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      <TextCardForm
        close={closeTextCardForm}
        isOpen={isTextCardFormOpen}
        setCurrentCanvas={setCurrentCanvas}
        startEditing={startEditing}
        canvasMode={canvasMode}
      />
      <DashboardForm
        isOpen={isSettingsOpen}
        close={closeSettings}
        dashboard={dashboard}
      />
      <ConfigureVariables
        isOpen={isConfigureVariablesOpen}
        close={closeConfigureVariables}
      />
      <ContentUsage
        isOpen={isUsageReportOpen}
        close={closeUsageReport}
        type={'dashboard'}
      />
      {filterBarSettings && (
        <FilterPlatesProvider dataTypes={canvasDataTypes}>
          <ReportQuickFiltersModal
            settings={filterBarSettings}
            isOpen={isQuickFiltersOpen}
            close={closeQuickFilters}
          />
          <PinnedFiltersModal
            settings={filterBarSettings}
            isOpen={isPinnedFiltersOpen}
            close={closePinnedFilters}
          />
        </FilterPlatesProvider>
      )}
      <ModalTransition>
        {isScheduleOpen && (
          <Modal onClose={closeSchedule}>
            <ScheduleReportModalContainer
              content={{
                id: dashboard.id,
                name: dashboard.name,
                type: 'dashboard',
                v3Access: dashboard.v3Access,
                isV3Published: dashboard.isV3Published,
                createdBy: dashboard.createdBy,
              }}
              createScheduleSavedFilter={createScheduleSavedFilter}
              close={closeSchedule}
            />
          </Modal>
        )}
      </ModalTransition>
    </React.Fragment>
  );
};

export default DashboardMenuContainer;
